import React, { useState, useEffect } from "react";
import {
  Camera,
  ClipboardList,
  Bot,
  X,
  Plus,
  Trash2,
  User,
  Calendar,
  AlertTriangle,
  Hospital,
  Save,
  Clock,
  FileText,
  Cake,
  Phone,
  Search,
} from "lucide-react";
import { usePacientes } from "../context/PacientesContext";
import { theme } from "../../../shared/styles/theme";
import { rotuloModalidade } from "../../../shared/utils/modalidadeAtendimento";

// Função para formatar data sem problema de timezone
const formatarData = (data: string | undefined): string => {
  if (!data) return "-";
  // Adiciona T12:00:00 para evitar problema de fuso horário
  const dataCorrigida = data.includes("T") ? data : `${data}T12:00:00`;
  return new Date(dataCorrigida).toLocaleDateString("pt-BR");
};

export default function ListaPacientes() {
  const { pacientes, selecionarPaciente } = usePacientes();
  const [buscaTexto, setBuscaTexto] = useState("");

  // Scroll para o topo ao abrir a lista
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // B351 (AI_152) — rótulo vem da fonte única (modalidadeAtendimento.ts) para não
  // haver duas listas de modalidades divergindo com o tempo.
  const getTipoLabel = (tipo: string) => {
    return rotuloModalidade(tipo);
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case "figado":
        return theme.tipoAtendimento.figado;
      case "rim":
        return theme.tipoAtendimento.rim;
      case "pancreas":
      case "pancreas_rim":
      case "rim_pancreas":
        return theme.tipoAtendimento.pancreas;
      case "doador_figado": // B351 (AI_152)
        return theme.tipoAtendimento.doador_figado;
      case "ambulatorial":
        return theme.tipoAtendimento.ambulatorial;
      default:
        return theme.tipoAtendimento.ambulatorial;
    }
  };

  // FILTRO DE BUSCA + ORDENAÇÃO ALFABÉTICA
  const pacientesFiltrados = pacientes
    .filter((paciente) => {
      const busca = buscaTexto.toLowerCase();
      return (
        paciente.nome.toLowerCase().includes(busca) ||
        (paciente.cpf || "").includes(busca)
      );
    })
    .sort((a, b) => a.nome.localeCompare(b.nome)); // ✅ ORDENAÇÃO ALFABÉTICA

  if (pacientes.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
          background: "white",
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
        }}
      >
        <ClipboardList
          size={80}
          color={theme.gray[300]}
          style={{ marginBottom: "20px" }}
        />
        <h3
          style={{
            fontSize: "24px",
            color: theme.gray[700],
            marginBottom: "10px",
            fontFamily: theme.fonts.sans,
          }}
        >
          Nenhum paciente cadastrado
        </h3>
        <p
          style={{
            color: theme.gray[500],
            fontSize: "16px",
            fontFamily: theme.fonts.sans,
          }}
        >
          Clique em "Novo Paciente" para adicionar o primeiro paciente
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER COM BUSCA */}
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* CAMPO DE BUSCA */}
        <div style={{ position: "relative", maxWidth: "500px" }}>
          <span
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "20px",
            }}
          >
            <Search size={16} />
          </span>
          <input
            type="text"
            placeholder="Buscar por nome ou CPF..."
            value={buscaTexto}
            onChange={(e) => setBuscaTexto(e.target.value)}
            style={{
              width: "100%",
              padding: "14px 16px 14px 48px",
              border: "2px solid #e5e7eb",
              borderRadius: "12px",
              fontSize: "15px",
              outline: "none",
              transition: "all 0.3s",
              fontFamily: theme.fonts.sans,
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = theme.primary[600];
              e.target.style.boxShadow = `0 0 0 3px ${theme.primary[100]}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e5e7eb";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* CONTADOR DE RESULTADOS */}
        {buscaTexto && (
          <p
            style={{
              fontSize: "14px",
              color: theme.gray[600],
              fontFamily: theme.fonts.sans,
              margin: 0,
            }}
          >
            {pacientesFiltrados.length === 0
              ? "Nenhum paciente encontrado"
              : `${pacientesFiltrados.length} paciente${
                  pacientesFiltrados.length !== 1 ? "s" : ""
                } encontrado${pacientesFiltrados.length !== 1 ? "s" : ""}`}
          </p>
        )}
      </div>

      {/* LISTA DE PACIENTES */}
      <div
        style={{
          background: "white",
          borderRadius: theme.borderRadius.xl,
          padding: "24px",
          boxShadow: theme.shadows.md,
        }}
      >
        {pacientesFiltrados.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
            }}
          >
            <Search
              size={60}
              color={theme.gray[300]}
              style={{ marginBottom: "16px" }}
            />
            <h3
              style={{
                fontSize: "20px",
                color: theme.gray[700],
                marginBottom: "8px",
                fontFamily: theme.fonts.sans,
              }}
            >
              Nenhum paciente encontrado
            </h3>
            <p
              style={{
                color: theme.gray[500],
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
              }}
            >
              Tente buscar por outro nome ou CPF
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "12px" }}>
            {pacientesFiltrados.map((paciente) => {
              const tipoColor = getTipoColor(paciente.tipoAtendimento || "");

              return (
                <div
                  key={paciente.id}
                  onClick={() => selecionarPaciente(paciente)}
                  style={{
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
                    border: `2px solid ${theme.gray[200]}`,
                    borderRadius: theme.borderRadius.lg,
                    padding: "16px 20px",
                    cursor: "pointer",
                    transition: theme.transitions.normal,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = theme.shadows.primary;
                    e.currentTarget.style.borderColor = theme.primary[500];
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = theme.gray[200];
                  }}
                >
                  {/* INFORMAÇÕES PRINCIPAIS */}
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: theme.gray[900],
                        margin: "0 0 8px 0",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {paciente.nome}
                    </h4>

                    <div
                      style={{
                        display: "flex",
                        gap: "24px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          color: theme.gray[600],
                          fontFamily: theme.fonts.mono,
                        }}
                      >
                        <FileText size={12} style={{ marginRight: "4px" }} />{" "}
                        {paciente.cpf}
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          color: theme.gray[600],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Cake size={12} style={{ marginRight: "4px" }} />{" "}
                        {formatarData(paciente.dataNascimento)}
                      </span>
                      {(paciente.telefone || paciente.telefone1) && (
                        <span
                          style={{
                            fontSize: "13px",
                            color: theme.gray[600],
                            fontFamily: theme.fonts.mono,
                          }}
                        >
                          <Phone size={12} style={{ marginRight: "4px" }} />{" "}
                          {paciente.telefone || paciente.telefone1}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* BADGE DO TIPO */}
                  <div
                    style={{
                      background: tipoColor.bg,
                      border: `2px solid ${tipoColor.border}`,
                      color: tipoColor.text,
                      padding: "6px 14px",
                      borderRadius: theme.borderRadius.full,
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: theme.fonts.sans,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getTipoLabel(paciente.tipoAtendimento || "")}
                  </div>

                  {/* BOTÕES */}
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      style={{
                        background: theme.gradients.primary,
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: theme.borderRadius.md,
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: theme.transitions.normal,
                        boxShadow: theme.shadows.primary,
                        fontFamily: theme.fonts.sans,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          theme.shadows.primaryHover;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = theme.shadows.primary;
                      }}
                    >
                      Ver detalhes →
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const previewUrl = window.location.origin;
                        window.open(
                          `${previewUrl}/prontuario/${paciente.id}`,
                          "_blank"
                        );
                      }}
                      style={{
                        background: "white",
                        border: `2px solid ${theme.primary[600]}`,
                        color: theme.primary[600],
                        padding: "8px 12px",
                        borderRadius: theme.borderRadius.md,
                        fontSize: "16px",
                        cursor: "pointer",
                        transition: theme.transitions.normal,
                      }}
                      title="Abrir em nova aba"
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = theme.primary[600];
                        e.currentTarget.style.color = "white";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = "white";
                        e.currentTarget.style.color = theme.primary[600];
                      }}
                    >
                      🗗
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
